import React, { useState } from 'react'
import { apis } from '@modules/apiClients'
import { UserService } from '@duck/api-definitions'
import { toast } from 'sonner'
import { StandardLoader } from '../StandardLoader'
import { useTar } from '@/modules'

interface ResendVerificationFormProps {
  onSuccess?: () => void
  onCancel?: () => void
  initialEmail?: string
  className?: string
}

export const ResendVerificationForm = ({
  onSuccess,
  onCancel,
  initialEmail = '',
  className = ''
}: ResendVerificationFormProps) => {
  const [email, setEmail] = useState(initialEmail)
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTar()
  const handleResendVerification = async () => {
    if (!email || !email.includes('@')) {
      toast.error(t('verification.enterValidEmail'))
      return
    }

    try {
      setIsLoading(true)

      const resendRequest: UserService.ResendVerificationRequest = { email }
      await apis.userService.resendVerificationPost(resendRequest)

      toast.success(t('verification.resendSuccess'))

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess()
      }

    } catch {

      toast.error(t('verification.resendFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="text-start">
        <label htmlFor="resend-email" className="block text-sm font-medium text-red-700 dark:text-red-300 mb-1">
          {t('verification.enterValidEmail')}
        </label>
        <input
          id="resend-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={"your@email.com"}
          disabled={isLoading}
          className="w-full px-3 py-2 border border-red-300 dark:border-red-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isLoading) {
              handleResendVerification()
            }
          }}
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={handleResendVerification}
          disabled={!email.includes('@') || isLoading}
          className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <StandardLoader size="sm" />
              {t('verification.sending')}
            </>
          ) : (
            t('verification.sendEmail')
          )}
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.cancel')}
          </button>
        )}
      </div>
    </div>
  )
}
